import type { Renderer2 } from '@angular/core';

export interface GridPagingDomContext {
    renderer: Renderer2;
    gridElement?: HTMLElement | null;
    gridId: string;
    paginationEnabled?: boolean;
}

interface StatusTextElements {
    iconElement: HTMLElement | null;
    textElement: HTMLElement | null;
    parentElement: HTMLElement | null;
}

export function addLoadDataInfoToPagination({
    renderer,
    gridElement,
    gridId,
    paginationEnabled,
    loadDataMethod,
    loadDataName,
}: GridPagingDomContext & { loadDataMethod?: unknown; loadDataName: string }): void {
    const context = { renderer, gridElement, gridId, paginationEnabled };
    const paging = getVisiblePagingPanel(context);
    const statusContainer = paging ?? getOrCreateStatusContainer(context);
    const dataTaskInfoId = getScopedElementId(context, String(loadDataMethod));

    if (!statusContainer || isNaN(Number(loadDataMethod)) || findElementById(gridElement, dataTaskInfoId)) return;

    const separator: HTMLSpanElement = renderer.createElement('span');
    separator.textContent = ':::';

    const dataTaskProcedure: HTMLSpanElement = renderer.createElement('span');
    dataTaskProcedure.textContent = loadDataMethod + ' - ' || ' - ';
    dataTaskProcedure.style.fontWeight = '600';

    const dataTaskName: HTMLSpanElement = renderer.createElement('span');
    dataTaskName.id = dataTaskInfoId;
    dataTaskName.textContent = loadDataName || '';

    renderer.insertBefore(dataTaskName, dataTaskProcedure, dataTaskName.childNodes[0]);

    if (paging) {
        renderer.insertBefore(paging, separator, paging.querySelector('.ag-paging-page-size'));
        renderer.insertBefore(paging, dataTaskName, separator);
    } else {
        renderer.appendChild(statusContainer, dataTaskName);
    }
}

export function addLoadingTextToPagingPanel({ show, text, ...context }: GridPagingDomContext & { show: boolean; text: string }): void {
    if (context.paginationEnabled === false) return;

    const elements = getOrCreateStatusTextElements(context, { iconId: getScopedElementId(context, 'loading-icon'), textId: getScopedElementId(context, 'loading-text'), elementId: getScopedElementId(context, 'loading-element') });

    if (show) {
        setStatusText(context.renderer, elements, text, 'far fa-spin fa-spinner-third');
    } else {
        hideStatusText(context.renderer, elements.parentElement);
    }
}

export function addSavingChangesTextToPagingPanel({ saving, translations, ...context }: GridPagingDomContext & { saving: string; translations: { saving: string; saved: string; error: string } }): void {
    if (context.paginationEnabled === false) return;

    const elements = getOrCreateStatusTextElements(context, { iconId: getScopedElementId(context, 'saving-icon'), textId: getScopedElementId(context, 'saving-text'), elementId: getScopedElementId(context, 'saving-element') });

    if (saving === 'saving') {
        setStatusText(context.renderer, elements, translations.saving, 'far fa-spin fa-spinner-third');
    } else if (saving === 'saved') {
        setStatusText(context.renderer, elements, translations.saved, 'far fa-check', 'var(--tt-success-color)');
    } else if (saving === 'error') {
        setStatusText(context.renderer, elements, translations.error, 'fas fa-exclamation-triangle', 'var(--tt-danger-color)');
    } else if (saving === 'none') {
        hideStatusText(context.renderer, elements.parentElement, 1000);
    }
}

function hideStatusText(renderer: Renderer2, parentElement: HTMLElement | null, delay: number = 0): void {
    if (parentElement)
        setTimeout(() => {
            renderer.setStyle(parentElement, 'opacity', 0);
            renderer.setStyle(parentElement, 'max-width', '0px');
        }, Math.abs(delay));
}

function getOrCreateStatusTextElements(context: GridPagingDomContext, { iconId, textId, elementId }: { iconId: string; textId: string; elementId: string }): StatusTextElements {
    const statusContainer = getOrCreateStatusContainer(context);
    let statusIcon = findElementById(context.gridElement, iconId);
    let statusText = findElementById(context.gridElement, textId);
    let statusElement = findElementById(context.gridElement, elementId);

    if (statusContainer) {
        if (!statusIcon || !statusText || !statusElement) {
            statusIcon = context.renderer.createElement('span');
            statusIcon!.id = iconId;

            statusText = context.renderer.createElement('span');
            statusText!.style.whiteSpace = 'nowrap';
            statusText!.id = textId;

            statusElement = createStatusElement(context.renderer, elementId, statusIcon, statusText);

            context.renderer.insertBefore(statusContainer, statusElement, statusContainer.children[0]);
        }
    }

    return { iconElement: statusIcon, textElement: statusText, parentElement: statusElement };
}

function getOrCreateStatusContainer(context: GridPagingDomContext): HTMLElement | null {
    const { renderer, gridElement } = context;
    const paging = getVisiblePagingPanel(context);
    const containerId = getStatusContainerId(context);
    let statusContainer = findElementById(gridElement, containerId);

    if (!statusContainer && (!!paging || !!gridElement)) {
        statusContainer = renderer.createElement('span');
        statusContainer!.id = containerId;
        statusContainer!.style.display = 'flex';
        statusContainer!.style.alignItems = 'center';
        statusContainer!.style.gap = '0.4rem';
        statusContainer!.style.transition = 'opacity 0.15s ease-in';

        if (paging) {
            statusContainer!.style.flex = '1';
            renderer.insertBefore(paging, statusContainer, paging.children[0]);
        } else {
            setupFloatingStatusContainer(renderer, gridElement!, statusContainer!);
            renderer.appendChild(gridElement, statusContainer);
        }
    }

    return statusContainer;
}

function createStatusElement(renderer: Renderer2, elementId: string, iconElement: HTMLElement | null, textElement: HTMLElement | null): HTMLElement {
    const statusElement = renderer.createElement('span');
    statusElement!.id = elementId;
    statusElement!.style.display = 'flex';
    statusElement!.style.alignItems = 'center';
    statusElement!.style.gap = '0.4rem';
    statusElement!.style.transition = 'opacity 0.15s ease-in';

    statusElement!.appendChild(iconElement!);
    statusElement!.appendChild(textElement!);

    return statusElement;
}

function setStatusText(renderer: Renderer2, { iconElement, textElement, parentElement }: StatusTextElements, text: string, icon: string, iconColor?: string): void {
    if (!!iconElement && !!textElement && !!parentElement) {
        renderer.setStyle(iconElement, 'color', iconColor ?? 'var(--tt-text-color)');
        renderer.setProperty(iconElement, 'className', icon);
        renderer.setProperty(textElement, 'textContent', text);
        renderer.setStyle(parentElement, 'max-width', 'fit-content');
        renderer.setStyle(parentElement, 'opacity', 1);
    }
}

function getPagingPanel(gridElement?: HTMLElement | null): HTMLDivElement | null {
    return gridElement?.querySelector<HTMLDivElement>('.ag-paging-panel') ?? null;
}

function getVisiblePagingPanel(context: GridPagingDomContext): HTMLDivElement | null {
    return context.paginationEnabled === false ? null : getPagingPanel(context.gridElement);
}

function getStatusContainerId(context: GridPagingDomContext): string {
    return getScopedElementId(context, 'status-container');
}

function getScopedElementId(context: GridPagingDomContext, suffix: string): string {
    return context.paginationEnabled === false ? context.gridId + 'floating-' + suffix : context.gridId + suffix;
}

function setupFloatingStatusContainer(renderer: Renderer2, gridElement: HTMLElement, statusContainer: HTMLElement): void {
    if (globalThis.getComputedStyle(gridElement).position === 'static') {
        renderer.setStyle(gridElement, 'position', 'relative');
    }

    statusContainer.style.position = 'absolute';
    statusContainer.style.right = '0.6rem';
    statusContainer.style.bottom = '0.4rem';
    statusContainer.style.zIndex = '2';
    statusContainer.style.justifyContent = 'flex-end';
    statusContainer.style.maxWidth = 'calc(100% - 1.2rem)';
    statusContainer.style.opacity = '0.55';
    statusContainer.style.padding = '0.2rem 0.4rem';
    statusContainer.style.pointerEvents = 'none';
    statusContainer.style.whiteSpace = 'nowrap';
}

function findElementById(rootElement: HTMLElement | null | undefined, elementId: string): HTMLElement | null {
    if (rootElement?.id === elementId) return rootElement;

    const scopedElement = Array.from(rootElement?.querySelectorAll<HTMLElement>('[id]') ?? []).find((element) => element.id === elementId);

    return scopedElement ?? document?.getElementById(elementId);
}
